/**
 * LinkedIn Outreach pricing — region aware (GBP / USD).
 * UK figures are the source of truth supplied by the business.
 * USD figures are sensible defaults rounded for clean price points;
 * swap them here at any time.
 */
import { isUK } from '@/config/region';

export interface LinkedInTier {
  id: 'monthly' | 'pilot' | 'scale';
  name: string;
  tagline: string;
  badge?: string;
  monthlyPrice: string;        // headline price shown big
  monthlySuffix: string;       // e.g. "/mo" or " total"
  setupFee: string;            // e.g. "£500 Setup Fee"
  setupWaived: boolean;        // strike through setup fee?
  dayOneLabel: string;         // "Day 1 cost"
  dayOneAmount: string;
  commitment: string;
  description: string;
  features: string[];
  ctaLabel: string;
  popular: boolean;
  /** Active promotion: original price renders struck through, these replace it. */
  promo?: {
    label: string;             // e.g. "50% off for new advisors"
    price: string;             // discounted headline price
    dayOneAmount: string;      // discounted day-one / effective figure
  };
}

const ukTiers: LinkedInTier[] = [
  {
    id: 'monthly',
    name: 'Month-to-Month',
    tagline: 'Maximum flexibility',
    monthlyPrice: '£895',
    monthlySuffix: '/mo',
    setupFee: '£500 Setup Fee',
    setupWaived: false,
    dayOneLabel: 'Day 1 cost',
    dayOneAmount: '£1,395',
    commitment: 'Rolling monthly — cancel anytime',
    description:
      'Ideal for firms that need full flexibility with no long-term commitment. A one-off technical setup fee covers the manual engineering of your target lists and AI conversation sequences.',
    features: [
      'AI conversation engine',
      'Target list build & enrichment',
      'LinkedIn warm-up sequencing',
      'Inbox monitoring & weekly reporting',
      'Cancel anytime',
    ],
    ctaLabel: 'Book a Discovery Call',
    popular: false,
  },
  {
    id: 'pilot',
    name: '90-Day Pilot',
    tagline: 'Our sweet spot',
    badge: 'Most Popular',
    monthlyPrice: '£795',
    monthlySuffix: '/mo',
    setupFee: '£500 Setup Fee',
    setupWaived: true,
    dayOneLabel: 'Day 1 cost',
    dayOneAmount: '£795',
    promo: {
      label: '50% off for new advisors',
      price: '£397.50',
      dayOneAmount: '£397.50',
    },
    commitment: '90-day commitment · billed monthly',
    description:
      'Our most popular entry point. A structured 90-day runway gives the AI time to safely warm up your LinkedIn, learn your ICP and start booking high-net-worth meetings. The setup fee is waived.',
    features: [
      'Everything in Month-to-Month',
      'Setup fee waived',
      '90-day AI learning runway',
      'Run multiple Targeted Campaigns',
      'Dedicated launch strategist',
      '30-Day Qualified Response Guarantee',
    ],
    ctaLabel: 'Book a Discovery Call',
    popular: true,
  },
  {
    id: 'scale',
    name: '6-Month Scale',
    tagline: 'Paid in full · best value',
    monthlyPrice: '£3,850',
    monthlySuffix: ' paid in full',
    setupFee: '£500 Setup Fee',
    setupWaived: true,
    dayOneLabel: 'Effective rate',
    dayOneAmount: '~£641/mo',
    promo: {
      label: '50% off for new advisors',
      price: '£1,925',
      dayOneAmount: '~£321/mo',
    },
    commitment: '6-month term · single payment',
    description:
      'The ultimate growth infrastructure for established advisory firms. Lock in 6 months at our lowest effective rate.',
    features: [
      'Everything in 90-Day Pilot',
      'Lowest effective rate',
      'Priority strategist support',
    ],
    ctaLabel: 'Book a Discovery Call',
    popular: false,
  },
];

const usTiers: LinkedInTier[] = [
  {
    id: 'monthly',
    name: 'Month-to-Month',
    tagline: 'Maximum flexibility',
    monthlyPrice: '$895',
    monthlySuffix: '/mo',
    setupFee: '$500 Setup Fee',
    setupWaived: false,
    dayOneLabel: 'Day 1 cost',
    dayOneAmount: '$1,395',
    commitment: 'Rolling monthly — cancel anytime',
    description:
      'Ideal for firms that need full flexibility with no long-term commitment. A one-off technical setup fee covers the manual engineering of your target lists and AI conversation sequences.',
    features: [
      'AI conversation engine',
      'Target list build & enrichment',
      'LinkedIn warm-up sequencing',
      'Inbox monitoring & weekly reporting',
      'Cancel anytime',
    ],
    ctaLabel: 'Book a Discovery Call',
    popular: false,
  },
  {
    id: 'pilot',
    name: '90-Day Pilot',
    tagline: 'Our sweet spot',
    badge: 'Most Popular',
    monthlyPrice: '$795',
    monthlySuffix: '/mo',
    setupFee: '$500 Setup Fee',
    setupWaived: true,
    dayOneLabel: 'Day 1 cost',
    dayOneAmount: '$795',
    promo: {
      label: '50% off for new advisors',
      price: '$397.50',
      dayOneAmount: '$397.50',
    },
    commitment: '90-day commitment · billed monthly',
    description:
      'Our most popular entry point. A structured 90-day runway gives the AI time to safely warm up your LinkedIn, learn your ICP and start booking high-net-worth meetings. The setup fee is waived.',
    features: [
      'Everything in Month-to-Month',
      'Setup fee waived',
      '90-day AI learning runway',
      'Run multiple Targeted Campaigns',
      'Dedicated launch strategist',
      '30-Day Qualified Response Guarantee',
    ],
    ctaLabel: 'Book a Discovery Call',
    popular: true,
  },
  {
    id: 'scale',
    name: '6-Month Scale',
    tagline: 'Paid in full · best value',
    monthlyPrice: '$3,850',
    monthlySuffix: ' paid in full',
    setupFee: '$500 Setup Fee',
    setupWaived: true,
    dayOneLabel: 'Effective rate',
    dayOneAmount: '~$641/mo',
    promo: {
      label: '50% off for new advisors',
      price: '$1,925',
      dayOneAmount: '~$321/mo',
    },
    commitment: '6-month term · single payment',
    description:
      'The ultimate growth infrastructure for established advisory firms. Lock in 6 months at our lowest effective rate.',
    features: [
      'Everything in 90-Day Pilot',
      'Lowest effective rate',
      'Priority strategist support',
    ],
    ctaLabel: 'Book a Discovery Call',
    popular: false,
  },
];

export const linkedinOutreachTiers: LinkedInTier[] = isUK ? ukTiers : usTiers;
